import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import isNull from 'lodash/isNull';
import startCase from 'lodash/startCase';
import styled from 'styled-components';
import {
  AlertContent,
  Avatar,
  Button,
  ButtonText,
  FieldWrapper,
  IconTypes,
  InputCharCounter,
  Label,
  List,
  ListItem,
  Message,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalLayout,
  ModalTitle,
  Select,
  StatusPayment,
  Text,
  Tooltip,
} from 'components';
import { ChangeDisbursementOptions } from 'config/const';
import { Appearance, Size, theme, toRem } from 'design';
import { routeForUserId } from 'routes';
import { clearRequest, selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { cancelPayoutRequest, changePayoutDisbursementMethodRequest, Payouts, retryPayoutRequest } from 'store/Collections/Payouts';
import { IStore } from 'store/types';
import { DisbursementMethod, IApiErrorPayloadWithCode, ICancelPayoutOptions, IChangeDisbursementMethodOptions, IPayout, PayoutStatus } from 'types';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay, formatJobTitle, formatPayPeriodLabel, isUndeliveredPayout } from 'utils';
import { HeaderContent, PayoutCard, PayoutCardRow, PayoutTimestamp, StyledFormError, Subtitle } from './styles';

interface IPayoutDetailsProps {
  payout: IPayout;
  onClose: () => void;
  onUpdate?: () => void;
}

const FormLayout = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${toRem(theme.space.xl)};
  background: ${theme.color.neutral[100]};
  border-radius: ${toRem(theme.borderRadius.small)};
  width: 100%;
  gap: ${toRem(theme.space.sm)};

  .optional {
    color: ${theme.color.neutral[400]};
  }
`;

const AlignRight = styled.span<{ capitalize?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  max-width: 90%;
  word-break: break-word;
  text-align: right;
  text-transform: ${({ capitalize }) => (capitalize ? 'capitalize' : 'inherit')};
  gap: ${toRem(theme.space.sm)};

  .change-button {
    color: ${theme.color.primary[500]};
    text-decoration: none;

    &:disabled {
      color: ${theme.color.neutral[500]};
      cursor: default;
    }
  }
`;

const TextWrap = styled.div`
  word-break: break-word;
`;

const ClosedPayPeriodMessage = styled(Message)`
  margin-top: ${toRem(theme.space.lg)};
  padding: ${toRem(theme.space.md)} ${toRem(theme.space.lg)};
`;

enum PayoutAction {
  CANCEL = 'cancel',
  CONVERT = 'convert',
}

const PayoutDetails = ({ payout, onClose, onUpdate }: IPayoutDetailsProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [action, setAction] = useState<PayoutAction>();
  const [cancelReason, setCancelReason] = useState<string>();
  const [convertPayload, setConvertPayload] = useState<IChangeDisbursementMethodOptions>();
  const retryErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Payouts.RETRY_PAYOUT_REQUEST)) as IApiErrorPayloadWithCode;
  const cancelErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Payouts.CANCEL_PAYOUT_REQUEST)) as IApiErrorPayloadWithCode;
  const changeDisbursementMethodErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Payouts.CHANGE_PAYOUT_DISBURSEMENT_METHOD_REQUEST)) as IApiErrorPayloadWithCode;
  const retryIsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payouts.RETRY_PAYOUT_REQUEST));
  const cancelIsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payouts.CANCEL_PAYOUT_REQUEST));
  const changeDisbursementMethodIsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payouts.CHANGE_PAYOUT_DISBURSEMENT_METHOD_REQUEST));

  useEffect(
    () => () => {
      dispatch(clearRequest(Payouts.RETRY_PAYOUT_REQUEST));
      dispatch(clearRequest(Payouts.CANCEL_PAYOUT_REQUEST));
      dispatch(clearRequest(Payouts.CHANGE_PAYOUT_DISBURSEMENT_METHOD_REQUEST));
    },
    [dispatch]
  );

  const lastUpdatedDate = payout?.updatedAt ? dayjs(payout.updatedAt).local().format('MM-DD-YYYY') : '--';
  const lastUpdatedTime = payout?.updatedAt ? dayjs(payout.updatedAt).local().format('HH:mm A z') : '--';
  const submittedDate = payout?.createdAt ? dayjs(payout.createdAt).local().format('MM-DD-YYYY') : '--';
  const submittedTime = payout?.createdAt ? dayjs(payout.createdAt).local().format('HH:mm A z') : '--';
  const sentBy = (payout?.statusHistory || []).find((status) => status.action === 'create')?.changedBy?.name || '';
  const isInClosedPayPeriod = payout?.payPeriodClosed;
  const isActionable = [PayoutStatus.EXPIRED, PayoutStatus.PENDING].includes(payout?.status);

  const retryPayment = () => {
    dispatch(retryPayoutRequest(payout.id, handleOnUpdate));
  };

  const confirmCancelPayment = (options: ICancelPayoutOptions) => {
    dispatch(cancelPayoutRequest(payout.id, options, handleOnUpdate));
  };

  const confirmChangeDisbursementMethod = (options: IChangeDisbursementMethodOptions) => {
    dispatch(changePayoutDisbursementMethodRequest(payout.id, options, handleOnUpdate));
  };

  const gotoEmployeeDetails = () => {
    history.push(routeForUserId(payout.employee.id));
  };

  const handleOnUpdate = () => {
    onClose();
    onUpdate?.();
  };

  // Footer
  const getFooter = (() => {
    if (isActionable) {
      return (
        <ModalFooter columns={2}>
          <Button data-cy="cancel-payment-btn" appearance="white" label="Cancel Payment" disabled={isInClosedPayPeriod} isLoading={retryIsLoading || cancelIsLoading} onClick={() => setAction(PayoutAction.CANCEL)} />
          <Button data-cy="retry-payment-btn" label="Retry Payment" disabled={cancelIsLoading} isProcessing={retryIsLoading} onClick={() => retryPayment()} />
        </ModalFooter>
      );
    }
    if (((payout && payout.status === PayoutStatus.CANCELED) || (payout && payout.status === PayoutStatus.PAID)) && payout && payout.employee.deletedAt === undefined) {
      return (
        <ModalFooter columns={1}>
          <Button
            appearance="primary"
            size="large"
            data-cy="view-all-transactions-btn"
            label="View All Transactions"
            onClick={() => {
              onClose();
              gotoEmployeeDetails();
            }}
          />
        </ModalFooter>
      );
    }
    return null;
  })();

  // Date Last X
  const DateLastActivity = (() => {
    if (payout && payout.status === PayoutStatus.CANCELED) {
      return <ListItem label="Date Canceled" data-cy="date-canceled" value={<PayoutTimestamp date={lastUpdatedDate || '--'} time={lastUpdatedTime || '--'} />} />;
    }
    if (payout && payout.status === PayoutStatus.PAID) {
      return <ListItem label="Date Paid" data-cy="date-paid" value={<PayoutTimestamp date={lastUpdatedDate || '--'} time={lastUpdatedTime || '--'} />} />;
    }
    return <ListItem label="Date Last Attempt" data-cy="date-last-attempt" value={<PayoutTimestamp date={lastUpdatedDate || '--'} time={lastUpdatedTime || '--'} />} />;
  })();

  const actionModalConfig = {
    [PayoutAction.CANCEL]: {
      dataCy: 'cancel-payment-modal',
      title: 'Cancel payment?',
      message: 'Are you sure you want to cancel this payment? This action cannot be undone.',
      rightActionLabel: 'Cancel Payment',
      isLoading: cancelIsLoading,
      onRightAction: () => confirmCancelPayment({ cancelReason }),
      children: (
        <FormLayout>
          <InputCharCounter label="Reason for Canceling" countMax={50} maxLength={50} onInputChange={({ value }) => setCancelReason(value)} />
          {cancelErrors?.errorMessages?.map((error, index) => (
            <StyledFormError key={`error-message-${index}`} data-cy="form-error">
              {error}
            </StyledFormError>
          ))}
        </FormLayout>
      ),
    },
    [PayoutAction.CONVERT]: {
      dataCy: 'payout-change-disbursement-method-modal',
      icon: 'Receipt' as IconTypes,
      iconFill: theme.color.neutral[400],
      title: 'Change Disbursement Method',
      message: 'Are you sure you want to change the disbursement method of this instant payment?',
      rightActionLabel: 'Save Changes',
      isLoading: changeDisbursementMethodIsLoading,
      disabled: !convertPayload?.type,
      onRightAction: () => confirmChangeDisbursementMethod(convertPayload),
      children: (
        <FormLayout>
          <FieldWrapper label="Disbursement Method">
            <Select
              data-cy="disbursement-input"
              placeholder=""
              options={ChangeDisbursementOptions}
              onChange={(selected) => setConvertPayload({ ...convertPayload, type: selected[0].value })}
              values={convertPayload?.type ? [convertPayload.type] : []}
            />
          </FieldWrapper>
          <InputCharCounter label="Reason" countMax={50} maxLength={50} optional onInputChange={({ value }) => setConvertPayload({ ...convertPayload, reason: value })} />
          {changeDisbursementMethodErrors?.errorMessages?.map((error, index) => (
            <StyledFormError key={`error-message-${index}`} data-cy="form-error">
              {error}
            </StyledFormError>
          ))}
        </FormLayout>
      ),
    },
  };

  const disbursementMethodValue = (disbursementMethod?: DisbursementMethod) => {
    if (!disbursementMethod) return '--';
    return (
      <AlignRight capitalize>
        {disbursementMethod}
        {isUndeliveredPayout(payout) && <ButtonText label="Change" data-cy="change-disbursement-method-btn" className="change-button" fontSize={11} disabled={isInClosedPayPeriod} onClick={() => setAction(PayoutAction.CONVERT)} />}
      </AlignRight>
    );
  };

  return action && actionModalConfig[action] ? (
    <AlertContent maxWidth={450} leftActionLabel="Back" onLeftAction={() => setAction(undefined)} {...actionModalConfig[action]}>
      {actionModalConfig[action].children}
    </AlertContent>
  ) : (
    <ModalLayout maxWidth={540} isActive onClose={onClose} data-cy="transaction-details-modal">
      <ModalHeader>
        <HeaderContent gridGap={theme.space.xl}>
          <Avatar
            size="modal"
            data-cy="avatar"
            hasLink
            id={(!payout?.employee.deletedAt && payout?.employee.id) || ''}
            userId={payout.userId}
            username={payout?.employee.name || ''}
            isLoading={!payout}
            isDismissed={!!payout?.employee.deletedAt}
          />

          <HeaderContent>
            <ModalTitle data-cy="modal-title">{payout?.employee.name || ''}</ModalTitle>
            <Subtitle data-cy="modal-subtitle">{payout?.id || ''}</Subtitle>
          </HeaderContent>
        </HeaderContent>

        <PayoutCardRow>
          <PayoutCard>
            <Label>Net Total</Label>
            <Text data-cy="net-total">{!isNull(payout?.net) ? centsToDollarsDisplay((payout as IPayout).net) : '--'}</Text>
          </PayoutCard>

          <PayoutCard data-cy="transaction-card" data-tooltip-id={`${payout?.id}-status-tooltip`}>
            <Label>Status</Label>

            {payout && (
              <>
                <StatusPayment size={Size.LARGE} status={payout.status} />
                {payout.alert && (
                  <Tooltip id={`${payout.id}-status-tooltip`} place="bottom" wrapper="p">
                    <strong>{startCase(payout.alert?.label)}</strong>
                    <br />
                    {payout.alert?.message}
                  </Tooltip>
                )}
              </>
            )}
          </PayoutCard>
        </PayoutCardRow>
      </ModalHeader>

      <ModalContent>
        <List>
          <ListItem label="Date Submitted" data-cy="date-submitted" value={<PayoutTimestamp date={submittedDate || '--'} time={submittedTime || '--'} />} />
          {DateLastActivity}
          <ListItem label="Tips" data-cy="tips" value={!isNil(payout?.gross) && !isNil(payout?.autoGratuity) ? centsToDollarsDisplay(payout.gross - payout.autoGratuity) : '--'} />
          <ListItem label="Autograts" data-cy="autogratuity" value={!isNil(payout?.autoGratuity) ? centsToDollarsDisplay(payout.autoGratuity) : '--'} />

          {payout && payout.status === PayoutStatus.CANCELED && (
            <ListItem label="Reason for Cancellation" data-cy="cancel-reason" accordion>
              {payout.cancelReason || 'No reason provided.'}
            </ListItem>
          )}

          <ListItem label="Gross" data-cy="gross" value={!isNil(payout?.gross) ? centsToDollarsDisplay((payout as IPayout).gross) : '--'} />
          <ListItem label="Holdback" data-cy="holdbacks" value={!isNil(payout?.holdback) ? centsToNegativeDollarsDisplay((payout as IPayout).holdback) : '--'} />
          <ListItem label="Net" data-cy="net" value={!isNil(payout?.net) ? centsToDollarsDisplay((payout as IPayout).net) : '--'} />
          <ListItem label="Site" data-cy="site" value={payout.site?.name ? <AlignRight>{payout.site.name}</AlignRight> : '--'} />
          <ListItem label="Disbursement Method" data-cy="disbursement-method" value={disbursementMethodValue(payout?.disbursementMethod)} />
          <ListItem label="Job Title" data-cy="job-title" value={payout && formatJobTitle(payout.job.title, payout.job.classification)} />
          <ListItem data-cy="sent-by" label="Sent By" value={sentBy ? <AlignRight>{sentBy}</AlignRight> : '--'} />
          <ListItem data-cy="notes" label="Notes" accordion>
            <TextWrap>{payout?.notes || 'No notes provided.'}</TextWrap>
          </ListItem>
          {payout?.approvalCheck.statusDesc && (
            <ListItem data-cy="review-notes" label="Reviewer's Note" accordion>
              <TextWrap>{payout?.approvalCheck.statusDesc}</TextWrap>
            </ListItem>
          )}
        </List>
      </ModalContent>

      {isActionable && isInClosedPayPeriod && (
        <ClosedPayPeriodMessage appearance={Appearance.WARNING} title="Pay Period Closed" message={`Payments for ${formatPayPeriodLabel(payout.payPeriod)} can't be canceled or changed.`} dataCy="closed-pay-period-message" />
      )}

      {retryErrors?.errorMessages?.map((error, index) => (
        <StyledFormError key={`error-message-${index}`} data-cy="form-error">
          {error}
        </StyledFormError>
      ))}

      {getFooter}
    </ModalLayout>
  );
};

export default PayoutDetails;
